import React, { useEffect, useState } from 'react';
import {
  Modal,
  TextInput,
  Radio,
  Group,
  Button,
  Stack,
  Text,
  Select,
  Avatar,
  Table,
  Title,
  LoadingOverlay,
  Badge,
  Paper,
  Box,
  Checkbox,
  Divider,
  Accordion,
  Pagination
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import {
  IconFile,
  IconSearch,
  IconArrowLeft,
  IconUserCheck,
  IconUsers,
  IconSchool,
  IconInfoCircle
} from '@tabler/icons-react';
import { Participant } from 'pages/app/LIA/AssessmentSchedules/utils/schema';
import { dataBinusService } from 'services/dataBinusService';
import useDebounce from 'hooks/useDebounce';
import httpClient from 'services/httpClient';
import { useDebouncedValue } from '@mantine/hooks';

interface Module {
  moduleId: string;
  moduleName: string;
  courseCode?: string;
  description?: string;
  beelajarId?: string | null;
}

interface BeelajarParticipant {
  userId: number;
  fullName?: string;
  fullname?: string;
  email: string;
}

interface CourseCertificate {
  id: number;
  name: string;
}

type BeelajarParticipantSource = 'attendance' | 'completion' | 'certificate';

interface ParticipantModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_selectedParticipants: Participant[], _selectedRespondents: Participant[]) => void;
  availableModules?: Module[];
  statusKurikulum?: boolean;
  /**
   * Limit selectable modules/courses to only those whose IDs are present in this array.
   * If not provided, falls back to all availableModules.
   */
  selectedModuleIds?: string[];
}

interface RespondentFilterOptions {
  isPeer: boolean;
  isSupervisor: boolean;
  isSubordinate: boolean;
}

const ParticipantModal: React.FC<ParticipantModalProps> = ({
  opened,
  onClose,
  onSubmit,
  availableModules = [],
  statusKurikulum = false,
  selectedModuleIds
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [type, setType] = useState('beelajar');
  const [participantType, setParticipantType] = useState<'Employee' | 'Lecturer'>('Employee');

  // Beelajar-related states
  const [beelajarCourseId, setBeelajarCourseId] = useState<string>('');
  const [beelajarSource, setBeelajarSource] = useState<BeelajarParticipantSource>('attendance');
  const [selectedAttendanceQR, setSelectedAttendanceQR] = useState<string | null>(null);
  const [selectedCertificate, setSelectedCertificate] = useState<string | null>(null);
  const [beelajarSearchQuery, setBeelajarSearchQuery] = useState('');
  const [debouncedBeelajarSearch] = useDebouncedValue(beelajarSearchQuery, 300);
  const [beelajarCurrentPage, setBeelajarCurrentPage] = useState(1);
  const [beelajarPageSize] = useState(15);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [selectedJobFamilyGroup, setSelectedJobFamilyGroup] = useState<string | null>(null);
  const [selectedJobFamily, setSelectedJobFamily] = useState<string | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRespondents, setSelectedRespondents] = useState<Participant[]>([]);
  const [activeTab, setActiveTab] = useState<'participants' | 'respondents'>('participants');
  const [showParticipantOptions, setShowParticipantOptions] = useState(false);

  const [participantModules, setParticipantModules] = useState<Record<string, string[]>>({});
  const [globalModules, setGlobalModules] = useState<string[]>([]);

  // Derive the modules actually displayed for assignment based on selectedModuleIds (if provided)
  const modulesForAssignment = React.useMemo(() => {
    if (!availableModules) return [] as Module[];
    if (!selectedModuleIds || selectedModuleIds.length === 0) return availableModules;
    return availableModules.filter((m) => selectedModuleIds.includes(m.moduleId));
  }, [availableModules, selectedModuleIds]);

  // Filter Beelajar courses from availableModules for the course selector
  const beelajarCoursesFromForm = React.useMemo(() => {
    if (!availableModules) return [];
    return availableModules.map((m) => ({
      value: String(m.beelajarId || m.moduleId),
      label: m.moduleName + (!m.beelajarId ? ' ⚠️ No Beelajar ID' : ''),
      disabled: !m.beelajarId
    }));
  }, [availableModules]);

  // Check if selected course has beelajarId
  const selectedCourseHasBeelajarId = React.useMemo(() => {
    if (!beelajarCourseId || !availableModules) return false;
    const selectedModule = availableModules.find(
      (m) => String(m.beelajarId) === beelajarCourseId || String(m.moduleId) === beelajarCourseId
    );
    return selectedModule?.beelajarId ? true : false;
  }, [beelajarCourseId, availableModules]);

  // Search filters for manual search
  const [searchEmployee, setSearchEmployee] = useState(true);
  const [searchLecturer, setSearchLecturer] = useState(false);

  // Respondent filter options
  const [respondentFilters, setRespondentFilters] = useState<RespondentFilterOptions>({
    isPeer: true,
    isSupervisor: true,
    isSubordinate: true
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const generateUniqueId = (participant: Participant) => {
    return `${participant.binusianId}__${participant.name || ''}__${participant.division || ''}__${participant.type || ''}`;
  };

  const toggleModule = (participantId: string, moduleId: string) => {
    setParticipantModules((prev) => {
      const currentModules = prev[participantId] || [];
      const newModules = currentModules.includes(moduleId)
        ? currentModules.filter((id) => id !== moduleId)
        : [...currentModules, moduleId];
      return {
        ...prev,
        [participantId]: newModules
      };
    });
  };

  const toggleGlobalModule = (moduleId: string) => {
    setGlobalModules((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    );
  };

  useEffect(() => {
    setSelectedBusinessUnit(null);
    setSelectedLevel(null);
    setSelectedJobFamilyGroup(null);
    setSelectedJobFamily(null);
    setSelectedParticipants([]);
    setSelectAll(false);
    setParticipantModules({});
    setGlobalModules([]);
  }, [participantType]);

  useEffect(() => {
    if (opened) {
      setType('beelajar');
      setParticipantType('Employee');
      setSelectedBusinessUnit(null);
      setSelectedLevel(null);
      setSelectedJobFamilyGroup(null);
      setSelectedJobFamily(null);
      setSearchQuery('');
      setSelectedParticipant(null);
      setSelectedParticipants([]);
      setSelectAll(false);
      setSelectedRespondents([]);
      setActiveTab('participants');
      setSearchEmployee(true);
      setSearchLecturer(false);
      setShowParticipantOptions(false);
      setParticipantModules({});
      setGlobalModules([]);

      // Reset Beelajar states
      setBeelajarCourseId('');
      setBeelajarSource('attendance');
      setSelectedAttendanceQR(null);
      setSelectedCertificate(null);
      setBeelajarSearchQuery('');
      setBeelajarCurrentPage(1);
    }
  }, [opened]);

  const { data: emplecData, isLoading: isLoadingEmplec } = useQuery({
    queryKey: ['listEmplec', debouncedSearchQuery, searchEmployee, searchLecturer],
    queryFn: () =>
      dataBinusService.getEmplecList({
        search: debouncedSearchQuery,
        isEmployee: searchEmployee,
        isLecturer: searchLecturer
      }),
    enabled: type === 'manual' && debouncedSearchQuery.length > 2
  });

  const { data: businessUnits, isLoading: isLoadingBusinessUnits } = useQuery({
    queryKey: ['businessUnits', participantType],
    queryFn: () => {
      if (participantType === 'Employee') {
        return dataBinusService.getListBU();
      } else {
        return dataBinusService.getListFakultas();
      }
    },
    enabled: type === 'hc'
  });

  const { data: levels, isLoading: isLoadingLevels } = useQuery({
    queryKey: ['levels', participantType],
    queryFn: () =>
      participantType === 'Lecturer'
        ? dataBinusService.getListLevel()
        : dataBinusService.getListBand(),
    enabled: type === 'hc'
  });

  const { data: jobFamilyGroups, isLoading: isLoadingJobFamilyGroups } = useQuery({
    queryKey: ['jobFamilyGroups'],
    queryFn: () => dataBinusService.getListJobFamilyGroups(),
    enabled: type === 'hc' && participantType === 'Employee'
  });

  const { data: jobFamilies, isLoading: isLoadingJobFamilies } = useQuery({
    queryKey: ['jobFamilies', selectedJobFamilyGroup],
    queryFn: () =>
      dataBinusService.getListJobFamilies({ jobFamilyGroupID: selectedJobFamilyGroup! }),
    enabled: type === 'hc' && participantType === 'Employee' && !!selectedJobFamilyGroup
  });

  const { data: hcParticipants, isLoading: isLoadingHCParticipants } = useQuery({
    queryKey: [
      'hcParticipants',
      participantType,
      selectedBusinessUnit,
      selectedLevel,
      selectedJobFamilyGroup,
      selectedJobFamily
    ],
    queryFn: () => {
      if (participantType === 'Employee') {
        return dataBinusService.getListEmployee({
          namagroupbu: selectedBusinessUnit,
          bandid: selectedLevel,
          jobFamilyGroupId: selectedJobFamilyGroup,
          jobFamilyId: selectedJobFamily
        });
      } else {
        return dataBinusService.getListLecturer({
          akademicGroup: selectedBusinessUnit,
          jjaId: selectedLevel
        });
      }
    },
    enabled: type === 'hc' && !!selectedBusinessUnit && !!selectedLevel
  });

  const { data: respondents, isLoading: isLoadingRespondents } = useQuery({
    queryKey: [
      'respondents',
      selectedParticipant?.email || selectedParticipant?.binusianId,
      respondentFilters
    ],
    queryFn: () =>
      dataBinusService.getRespondentList({
        email: selectedParticipant?.email || selectedParticipant?.binusianId || '',
        ispeer: respondentFilters.isPeer,
        isspv: respondentFilters.isSupervisor,
        issubordinate: respondentFilters.isSubordinate
      }),
    enabled: !!selectedParticipant && activeTab === 'respondents'
  });

  // Beelajar queries
  const { data: qrAttendanceList, isLoading: isLoadingQRList } = useQuery({
    queryKey: ['qrAttendanceList', beelajarCourseId],
    queryFn: async () => {
      const response = await httpClient.get('/assessment-schedules/qr-participants', {
        params: {
          pageNumber: 1,
          pageSize: 15,
          courseid: beelajarCourseId,
          attendanceqrid: ''
        }
      });
      return response.data;
    },
    enabled: opened && type === 'beelajar' && beelajarSource === 'attendance' && !!beelajarCourseId
  });

  const { data: certificatesList, isLoading: isLoadingCertificates } = useQuery({
    queryKey: ['courseCertificates', beelajarCourseId],
    queryFn: async () => {
      const response = await httpClient.get('/assessment-schedules/customcert', {
        params: {
          pageNumber: 1,
          pageSize: 100,
          courseid: beelajarCourseId
        }
      });
      return response.data;
    },
    enabled: opened && type === 'beelajar' && beelajarSource === 'certificate' && !!beelajarCourseId
  });

  const { data: allBeelajarParticipantsData, isLoading: isLoadingBeelajarParticipants } = useQuery({
    queryKey: [
      'beelajarAllParticipants',
      beelajarCourseId,
      beelajarSource,
      selectedAttendanceQR,
      selectedCertificate
    ],
    queryFn: async () => {
      let endpoint = '';
      let params: any = {
        pageNumber: 1,
        pageSize: 1000,
        courseid: beelajarCourseId
      };

      switch (beelajarSource) {
        case 'attendance':
          if (!selectedAttendanceQR) return null;
          endpoint = '/assessment-schedules/qr-participants';
          params.attendanceqrid = selectedAttendanceQR;
          break;
        case 'completion':
          endpoint = '/assessment-schedules/course-participants';
          break;
        case 'certificate':
          if (!selectedCertificate) return null;
          endpoint = '/assessment-schedules/customcert';
          params.customcertid = selectedCertificate;
          break;
        default:
          return null;
      }

      const response = await httpClient.get(endpoint, { params });
      return response.data;
    },
    enabled:
      opened &&
      type === 'beelajar' &&
      !!beelajarCourseId &&
      ((beelajarSource === 'attendance' && !!selectedAttendanceQR) ||
        (beelajarSource === 'certificate' && !!selectedCertificate) ||
        beelajarSource === 'completion')
  });

  const businessUnitOptions =
    businessUnits?.data
      ?.map((item: any) => {
        if (participantType === 'Employee') {
          return { value: item.businessUnit, label: item.businessUnit };
        } else {
          return { value: item.fakultasID, label: item.fakultasDesc };
        }
      })
      .filter((option) => option.value != null && option.label != null)
      .filter((option, index, self) => self.findIndex((o) => o.value === option.value) === index) ||
    [];

  const levelOptions =
    levels?.data
      ?.map((item: any) => ({
        value: participantType === 'Lecturer' ? item.levelID : item.bandID,
        label: participantType === 'Lecturer' ? item.levelName : item.bandID
      }))
      .filter((option) => option.value != null && option.label != null) || [];

  const jobFamilyGroupOptions =
    jobFamilyGroups?.data
      ?.map((item: any) => ({
        value: item.jobFamilyGroupID,
        label: item.jobFamilyGroupName
      }))
      .filter((option) => option.value != null && option.label != null) || [];

  const jobFamilyOptions =
    jobFamilies?.data
      ?.map((item: any) => ({
        value: item.jobFamilyID,
        label: item.jobFamilyName
      }))
      .filter((option) => option.value != null && option.label != null) || [];

  const mappedHCParticipants =
    hcParticipants?.data?.map((item: any) => {
      if (participantType === 'Employee') {
        return {
          binusianId: item.binusianId || '',
          name: item.nama || item.name,
          division: item.bu || item.division,
          type: item.level || 'Employee',
          email: item.email
        };
      } else {
        return {
          binusianId: item.binusianId || item.email,
          name: item.nama || item.name,
          division: item.fakultas || item.division,
          type: item.level || 'Lecturer',
          email: item.email
        };
      }
    }) || [];

  const employeeParticipants: Participant[] = [];
  if (emplecData?.employees?.data) {
    emplecData.employees.data.forEach((emp: any) => {
      employeeParticipants.push({
        binusianId: emp.binusianId,
        name: emp.name,
        division: emp.bu || 'Employee',
        type: 'Employee',
        email: emp.email
      });
    });
  }

  const lecturerParticipants: Participant[] = [];
  if (emplecData?.lecturers?.data) {
    emplecData.lecturers.data.forEach((lec: any) => {
      lecturerParticipants.push({
        binusianId: lec.binusianId,
        name: lec.name,
        division: lec.fakultas || 'Lecturer',
        type: 'Lecturer',
        email: lec.email
      });
    });
  }

  // Process Beelajar participants
  const { filteredBeelajarParticipants, allFilteredBeelajarParticipants, totalBeelajarPages } =
    React.useMemo(() => {
      const allParticipants: Participant[] = (allBeelajarParticipantsData?.data || []).map(
        (item: BeelajarParticipant) => ({
          binusianId: item.userId.toString(),
          name: item.fullName || item.fullname || 'Unknown Name',
          email: item.email || '',
          type: 'Beelajar',
          division: 'Beelajar Course'
        })
      );

      let filtered = allParticipants;
      if (debouncedBeelajarSearch.trim()) {
        const searchLower = debouncedBeelajarSearch.toLowerCase();
        filtered = allParticipants.filter(
          (participant) =>
            participant.name.toLowerCase().includes(searchLower) ||
            (participant.email ?? '').toLowerCase().includes(searchLower) ||
            participant.binusianId.toLowerCase().includes(searchLower)
        );
      }

      const total = Math.ceil(filtered.length / beelajarPageSize);
      const startIndex = (beelajarCurrentPage - 1) * beelajarPageSize;
      const endIndex = startIndex + beelajarPageSize;
      const paginatedParticipants = filtered.slice(startIndex, endIndex);

      return {
        filteredBeelajarParticipants: paginatedParticipants,
        allFilteredBeelajarParticipants: filtered,
        totalBeelajarParticipants: filtered.length,
        totalBeelajarPages: total
      };
    }, [
      allBeelajarParticipantsData?.data,
      debouncedBeelajarSearch,
      beelajarCurrentPage,
      beelajarPageSize
    ]);

  // Process Beelajar dropdown options
  const qrAttendanceOptions = (qrAttendanceList?.data || [])
    .map((item: any) => {
      const value = (item.attendanceQRId ?? item.id ?? item.userId)?.toString();
      const name = item.attendanceQRName || item.name || item.fullName || 'Unknown Session';
      const dateRaw = item.date || item.createdDate;
      const label = dateRaw ? `${name} - ${new Date(dateRaw).toLocaleDateString()}` : name;
      return { value, label };
    })
    .filter((option: any) => option.value !== undefined);

  const certificateOptions = (certificatesList?.data || []).map((cert: CourseCertificate) => ({
    value: cert.id.toString(),
    label: cert.name
  }));

  // Check if we should show the Beelajar search input
  const shouldShowBeelajarSearch =
    (beelajarSource === 'attendance' && selectedAttendanceQR) ||
    (beelajarSource === 'certificate' && selectedCertificate) ||
    beelajarSource === 'completion';

  // Reset to first page when Beelajar search changes
  React.useEffect(() => {
    setBeelajarCurrentPage(1);
  }, [debouncedBeelajarSearch]);

  const handleSelectParticipant = (participant: Participant) => {
    setSelectedParticipant(participant);
    setSelectedParticipants([participant]);
    setShowParticipantOptions(true);
  };

  const handleToggleParticipant = (participant: Participant) => {
    setSelectedParticipants((prev) => {
      const isSelected = prev.some((p) => p.binusianId === participant.binusianId);
      if (isSelected) {
        return prev.filter((p) => p.binusianId !== participant.binusianId);
      } else {
        return [...prev, participant];
      }
    });
  };

  const handleBeelajarSelectAll = () => {
    const allSelected = filteredBeelajarParticipants.every((p) =>
      selectedParticipants.some((selected) => selected.binusianId === p.binusianId)
    );

    if (allSelected) {
      setSelectedParticipants((prev) =>
        prev.filter(
          (selected) =>
            !filteredBeelajarParticipants.some((p) => p.binusianId === selected.binusianId)
        )
      );
    } else {
      const newSelections = filteredBeelajarParticipants.filter(
        (p) => !selectedParticipants.some((selected) => selected.binusianId === p.binusianId)
      );
      setSelectedParticipants((prev) => [...prev, ...newSelections]);
    }
  };

  const handleBeelajarSelectAllPages = () => {
    const allSelected = allFilteredBeelajarParticipants.every((p) =>
      selectedParticipants.some((selected) => selected.binusianId === p.binusianId)
    );

    if (allSelected) {
      setSelectedParticipants((prev) =>
        prev.filter(
          (selected) =>
            !allFilteredBeelajarParticipants.some((p) => p.binusianId === selected.binusianId)
        )
      );
    } else {
      const newSelections = allFilteredBeelajarParticipants.filter(
        (p) => !selectedParticipants.some((selected) => selected.binusianId === p.binusianId)
      );
      setSelectedParticipants((prev) => [...prev, ...newSelections]);
    }
  };

  const handleSelectAll = (participants: Participant[]) => {
    if (selectAll) {
      setSelectedParticipants([]);
    } else {
      setSelectedParticipants(participants);
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setSelectAll(
      selectedParticipants.length === mappedHCParticipants.length && mappedHCParticipants.length > 0
    );
  }, [selectedParticipants, mappedHCParticipants]);

  const handleAddParticipantOnly = () => {
    if (selectedParticipants.length > 0) {
      const participantsWithModules = selectedParticipants.map((participant) => {
        const participantSpecificModules = participantModules[participant.binusianId] || [];
        const allModules = [...new Set([...globalModules, ...participantSpecificModules])];
        return {
          ...participant,
          listModule: allModules
        };
      });
      onSubmit(participantsWithModules, []);
      onClose();
    }
  };

  const handleToggleRespondent = (respondent: Participant) => {
    setSelectedRespondents((prev) => {
      const isSelected = prev.some((r) => r.binusianId === respondent.binusianId);

      if (isSelected) {
        return prev.filter((r) => r.binusianId !== respondent.binusianId);
      } else {
        const updatedRespondent = {
          ...respondent,
          participantId: selectedParticipant?.binusianId
        };
        return [...prev, updatedRespondent];
      }
    });
  };

  const handleSubmit = () => {
    if (selectedParticipant) {
      const participantSpecificModules = participantModules[selectedParticipant.binusianId] || [];
      const allModules = [...new Set([...globalModules, ...participantSpecificModules])];
      const participantWithModules = {
        ...selectedParticipant,
        listModule: allModules
      };

      const updatedRespondents = selectedRespondents.map((respondent) => {
        const withParticipantId = {
          ...respondent,
          participantId: selectedParticipant.binusianId
        };
        return withParticipantId;
      });

      onSubmit([participantWithModules], updatedRespondents);
    }
    onClose();
  };

  const handleBackToParticipants = () => {
    setActiveTab('participants');
    setSelectedParticipant(null);
    setSelectedRespondents([]);
    setShowParticipantOptions(false);
  };

  // Function to render participant table
  const renderParticipantTable = (
    participants: Participant[],
    participantType: string,
    isMultiple: boolean = false,
    onToggle?: (_participant: Participant) => void,
    onSelectAll?: () => void,
    selectedParticipants?: Participant[],
    selectAll?: boolean
  ) => {
    if (participants.length === 0) {
      return (
        <Text c='dimmed' ta='center' mt='md'>
          No {participantType} found matching your search
        </Text>
      );
    }

    return (
      <Table striped border={1} mt='md'>
        <Table.Thead>
          <Table.Tr>
            {isMultiple && (
              <Table.Th style={{ width: 50 }}>
                <Checkbox
                  checked={selectAll}
                  onChange={onSelectAll}
                  indeterminate={
                    selectedParticipants && selectedParticipants.length > 0 && !selectAll
                  }
                />
              </Table.Th>
            )}
            <Table.Th>Name</Table.Th>
            <Table.Th>Division</Table.Th>
            <Table.Th>Type</Table.Th>
            {!isMultiple && <Table.Th style={{ width: 120 }}>Actions</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {participants.map((participant) => (
            <Table.Tr key={participant.binusianId}>
              {isMultiple && (
                <Table.Td>
                  <Checkbox
                    checked={
                      selectedParticipants?.some((p) => p.binusianId === participant.binusianId) ||
                      false
                    }
                    onChange={() => onToggle?.(participant)}
                  />
                </Table.Td>
              )}
              <Table.Td>
                <Group>
                  <Avatar color='#7B2183' radius='xl'>
                    {participant.name && participant.name.length > 0
                      ? participant.name.charAt(0)
                      : '?'}
                  </Avatar>
                  <div>
                    <Text size='sm' fw={500}>
                      {participant.name || 'Unknown Name'}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      {participant.binusianId}
                    </Text>
                  </div>
                </Group>
              </Table.Td>
              <Table.Td>{participant.division || 'N/A'}</Table.Td>
              <Table.Td>{participant.type || 'Unknown'}</Table.Td>
              {!isMultiple && (
                <Table.Td>
                  <Button
                    size='xs'
                    leftSection={<IconUserCheck size={16} />}
                    onClick={() => handleSelectParticipant(participant)}
                    color='#7B2183'
                  >
                    Choose
                  </Button>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Modal opened={opened} centered onClose={onClose} size='xl' withCloseButton={false}>
      <Stack gap='md' p={16}>
        <Title order={3}>
          {activeTab === 'participants'
            ? 'Add Participant'
            : `Select Respondent for ${selectedParticipant?.name || 'Selected Participant'}`}
        </Title>

        {activeTab === 'participants' && !showParticipantOptions && (
          <>
            <Group grow>
              <Button
                variant='outline'
                leftSection={<IconSchool size={16} />}
                onClick={() => setType('beelajar')}
                color={type === 'beelajar' ? '#7B2183' : 'gray'}
              >
                Search from Beelajar LMS
              </Button>
              <Button
                variant='outline'
                leftSection={<IconSearch size={16} />}
                onClick={() => setType('manual')}
                color={type === 'manual' ? '#7B2183' : 'gray'}
              >
                Manual Search Participant
              </Button>
              <Button
                variant='outline'
                leftSection={<IconFile size={16} />}
                onClick={() => setType('hc')}
                color={type === 'hc' ? '#7B2183' : 'gray'}
              >
                Generate From HC Data
              </Button>
            </Group>

            {type === 'beelajar' ? (
              <div style={{ position: 'relative' }}>
                <LoadingOverlay
                  visible={
                    isLoadingQRList || isLoadingCertificates || isLoadingBeelajarParticipants
                  }
                  overlayProps={{ radius: 'sm', blur: 2 }}
                />

                {beelajarCoursesFromForm.length === 0 ? (
                  <Paper withBorder p='md' bg='orange.0'>
                    <Text size='sm' c='orange.9' fw={500}>
                      No courses available
                    </Text>
                    <Text size='xs' c='dimmed' mt='xs'>
                      Please add courses/modules to the assessment schedule first before searching
                      for participants from Beelajar LMS.
                    </Text>
                  </Paper>
                ) : (
                  <Select
                    label='Select Course from Assessment Schedule'
                    placeholder='Choose a course'
                    value={beelajarCourseId}
                    onChange={(value) => {
                      setBeelajarCourseId(value || '');
                      setSelectedParticipants([]);
                      setBeelajarCurrentPage(1);
                      setSelectedAttendanceQR(null);
                      setSelectedCertificate(null);
                      setBeelajarSearchQuery('');
                    }}
                    data={beelajarCoursesFromForm}
                    searchable
                    required
                    description='Select one of the courses already added to this assessment schedule'
                  />
                )}

                {beelajarCourseId && (
                  <>
                    {!selectedCourseHasBeelajarId && (
                      <Paper withBorder p='md' bg='red.0' mt='sm'>
                        <Group gap='xs'>
                          <IconInfoCircle size={20} color='var(--mantine-color-red-7)' />
                          <div style={{ flex: 1 }}>
                            <Text size='sm' c='red.9' fw={500}>
                              This course doesn't have a Beelajar ID
                            </Text>
                            <Text size='xs' c='dimmed' mt='xs'>
                              This course is not linked to Beelajar LMS and cannot be used to fetch
                              participants. Please use the "Search Manually" or "HC Data" tabs
                              instead, or contact your administrator to link this course to
                              Beelajar.
                            </Text>
                          </div>
                        </Group>
                      </Paper>
                    )}

                    <Select
                      mt='sm'
                      label='Select Participant Source'
                      placeholder='Choose how to select participants'
                      value={beelajarSource}
                      onChange={(value) => {
                        setBeelajarSource(value as BeelajarParticipantSource);
                        setSelectedParticipants([]);
                        setBeelajarCurrentPage(1);
                        setSelectedAttendanceQR(null);
                        setSelectedCertificate(null);
                        setBeelajarSearchQuery('');
                      }}
                      data={[
                        { value: 'attendance', label: 'Attendance QR Course' },
                        { value: 'completion', label: 'Completion Course' },
                        { value: 'certificate', label: 'Course Certificate' }
                      ]}
                      disabled={!selectedCourseHasBeelajarId}
                    />

                    {beelajarSource === 'attendance' && selectedCourseHasBeelajarId && (
                      <>
                        <Select
                          mt='sm'
                          label='Select QR Attendance Session'
                          placeholder='Choose QR attendance session'
                          value={selectedAttendanceQR}
                          onChange={setSelectedAttendanceQR}
                          data={qrAttendanceOptions}
                          disabled={isLoadingQRList}
                          searchable
                          required
                        />
                        {!isLoadingQRList && qrAttendanceOptions.length === 0 && (
                          <Paper withBorder p='md' bg='yellow.0' mt='sm'>
                            <Group gap='xs'>
                              <IconInfoCircle size={20} color='var(--mantine-color-yellow-7)' />
                              <div style={{ flex: 1 }}>
                                <Text size='sm' c='yellow.9' fw={500}>
                                  No QR Attendance Sessions Found
                                </Text>
                                <Text size='xs' c='dimmed' mt='xs'>
                                  This course doesn't have any QR attendance sessions yet. You still
                                  can try using "Completion Course" or "Course Certificate" options
                                  instead.
                                </Text>
                              </div>
                            </Group>
                          </Paper>
                        )}
                      </>
                    )}

                    {beelajarSource === 'certificate' && selectedCourseHasBeelajarId && (
                      <Select
                        mt='sm'
                        label='Select Course Certificate'
                        placeholder='Choose course certificate'
                        value={selectedCertificate}
                        onChange={(value) => {
                          setSelectedCertificate(value);
                          setBeelajarCurrentPage(1);
                        }}
                        data={certificateOptions}
                        disabled={isLoadingCertificates}
                        searchable
                        required
                      />
                    )}

                    {shouldShowBeelajarSearch && selectedCourseHasBeelajarId && (
                      <>
                        <TextInput
                          mt='sm'
                          label='Search Participants'
                          placeholder='Search by name, email, or ID...'
                          value={beelajarSearchQuery}
                          onChange={(e) => setBeelajarSearchQuery(e.currentTarget.value)}
                          leftSection={<IconSearch size={16} />}
                        />

                        {debouncedBeelajarSearch && (
                          <Paper withBorder p='sm' bg='gray.0' mt='sm'>
                            <Text size='sm' c='dimmed'>
                              {filteredBeelajarParticipants.length > 0
                                ? `Found ${filteredBeelajarParticipants.length} participants matching "${debouncedBeelajarSearch}"`
                                : `No participants found matching "${debouncedBeelajarSearch}"`}
                            </Text>
                          </Paper>
                        )}

                        {selectedParticipants.length > 0 && (
                          <Paper withBorder p='sm' bg='blue.0' mt='sm'>
                            <Group justify='space-between'>
                              <Text size='sm' fw={500}>
                                <IconUsers size={16} style={{ marginRight: 8 }} />
                                {selectedParticipants.length} participant(s) selected
                              </Text>
                              <Button
                                size='xs'
                                variant='subtle'
                                color='blue'
                                onClick={() => setSelectedParticipants([])}
                              >
                                Clear All
                              </Button>
                            </Group>
                          </Paper>
                        )}

                        {filteredBeelajarParticipants.length > 0 && (
                          <>
                            <Group mb='sm' justify='space-between' mt='md'>
                              <Group gap='md'>
                                <Checkbox
                                  label={`Select all on this page (${filteredBeelajarParticipants.length})`}
                                  checked={filteredBeelajarParticipants.every((p) =>
                                    selectedParticipants.some(
                                      (selected) => selected.binusianId === p.binusianId
                                    )
                                  )}
                                  indeterminate={
                                    filteredBeelajarParticipants.some((p) =>
                                      selectedParticipants.some(
                                        (selected) => selected.binusianId === p.binusianId
                                      )
                                    ) &&
                                    !filteredBeelajarParticipants.every((p) =>
                                      selectedParticipants.some(
                                        (selected) => selected.binusianId === p.binusianId
                                      )
                                    )
                                  }
                                  onChange={handleBeelajarSelectAll}
                                />
                                {totalBeelajarPages > 1 && (
                                  <Button
                                    size='xs'
                                    variant={
                                      allFilteredBeelajarParticipants.every((p) =>
                                        selectedParticipants.some(
                                          (selected) => selected.binusianId === p.binusianId
                                        )
                                      )
                                        ? 'filled'
                                        : 'outline'
                                    }
                                    color='#7B2183'
                                    onClick={handleBeelajarSelectAllPages}
                                  >
                                    {allFilteredBeelajarParticipants.every((p) =>
                                      selectedParticipants.some(
                                        (selected) => selected.binusianId === p.binusianId
                                      )
                                    )
                                      ? `Deselect All (${allFilteredBeelajarParticipants.length})`
                                      : `Select All (${allFilteredBeelajarParticipants.length})`}
                                  </Button>
                                )}
                              </Group>
                              <Badge variant='light' color='blue'>
                                Page {beelajarCurrentPage} of {totalBeelajarPages}
                              </Badge>
                            </Group>

                            <Table striped border={1}>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th style={{ width: 50 }}>Select</Table.Th>
                                  <Table.Th>Name</Table.Th>
                                  <Table.Th>Email</Table.Th>
                                  <Table.Th>Type</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {filteredBeelajarParticipants.map((participant) => (
                                  <Table.Tr key={participant.binusianId}>
                                    <Table.Td>
                                      <Checkbox
                                        checked={selectedParticipants.some(
                                          (p) => p.binusianId === participant.binusianId
                                        )}
                                        onChange={() => handleToggleParticipant(participant)}
                                      />
                                    </Table.Td>
                                    <Table.Td>
                                      <Group>
                                        <Avatar color='#7B2183' radius='xl'>
                                          {participant.name && participant.name.length > 0
                                            ? participant.name.charAt(0)
                                            : '?'}
                                        </Avatar>
                                        <div>
                                          <Text size='sm' fw={500}>
                                            {participant.name || 'Unknown Name'}
                                          </Text>
                                          <Text size='xs' c='dimmed'>
                                            {participant.binusianId}
                                          </Text>
                                        </div>
                                      </Group>
                                    </Table.Td>
                                    <Table.Td>{participant.email || 'N/A'}</Table.Td>
                                    <Table.Td>{participant.type || 'Unknown'}</Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>

                            {totalBeelajarPages > 1 && (
                              <Group justify='center' mt='md'>
                                <Pagination
                                  total={totalBeelajarPages}
                                  value={beelajarCurrentPage}
                                  onChange={setBeelajarCurrentPage}
                                  color='#7B2183'
                                />
                              </Group>
                            )}

                            {selectedParticipants.length > 0 && (
                              <Group justify='flex-end' mt='md'>
                                <Button
                                  color='#7B2183'
                                  onClick={() => setShowParticipantOptions(true)}
                                >
                                  Proceed with {selectedParticipants.length} Selected Participant
                                  {selectedParticipants.length !== 1 ? 's' : ''}
                                </Button>
                              </Group>
                            )}
                          </>
                        )}

                        {!isLoadingBeelajarParticipants &&
                          filteredBeelajarParticipants.length === 0 && (
                            <Text c='dimmed' ta='center' mt='md'>
                              No participants found for the selected criteria
                            </Text>
                          )}
                      </>
                    )}

                    {!shouldShowBeelajarSearch && (
                      <Text c='dimmed' ta='center' mt='md'>
                        Please select{' '}
                        {beelajarSource === 'attendance'
                          ? 'an attendance session'
                          : 'a certificate'}{' '}
                        to view participants
                      </Text>
                    )}
                  </>
                )}

                {!beelajarCourseId && beelajarCoursesFromForm.length > 0 && (
                  <Paper withBorder p='sm' bg='blue.0' mt='sm'>
                    <Text size='sm' c='blue.9'>
                      ℹ️ Select a course to search for participants from Beelajar LMS. Courses
                      marked with ⚠️ don't have Beelajar integration and cannot be used here.
                    </Text>
                  </Paper>
                )}
              </div>
            ) : type === 'hc' ? (
              <div style={{ position: 'relative' }}>
                <LoadingOverlay
                  visible={
                    isLoadingBusinessUnits ||
                    isLoadingLevels ||
                    isLoadingJobFamilyGroups ||
                    isLoadingJobFamilies
                  }
                  overlayProps={{ radius: 'sm', blur: 2 }}
                />

                <Radio.Group
                  label='Select Type'
                  value={participantType}
                  onChange={(value) => setParticipantType(value as 'Employee' | 'Lecturer')}
                >
                  <Group mt='xs'>
                    <Radio value='Employee' label='Employee' />
                    <Radio value='Lecturer' label='Lecturer' />
                  </Group>
                </Radio.Group>

                <Select
                  mt='sm'
                  label={participantType === 'Employee' ? 'Business Unit' : 'Faculty'}
                  placeholder={`Select ${participantType === 'Employee' ? 'Business Unit' : 'Faculty'}`}
                  data={businessUnitOptions}
                  value={selectedBusinessUnit}
                  onChange={setSelectedBusinessUnit}
                  searchable
                  disabled={isLoadingBusinessUnits}
                  key={`businessUnit-${participantType}`}
                />

                <Select
                  mt={'sm'}
                  label='Level'
                  placeholder='Select Level'
                  data={levelOptions}
                  value={selectedLevel}
                  onChange={setSelectedLevel}
                  searchable
                  disabled={isLoadingLevels || !selectedBusinessUnit}
                  key={`level-${participantType}`}
                />

                {participantType === 'Employee' && (
                  <>
                    <Select
                      mt={'sm'}
                      label='Job Family Group'
                      placeholder='Select Job Family Group'
                      data={jobFamilyGroupOptions}
                      value={selectedJobFamilyGroup}
                      onChange={(value) => {
                        setSelectedJobFamilyGroup(value);
                        setSelectedJobFamily(null); // Reset job family when group changes
                      }}
                      searchable
                      disabled={isLoadingJobFamilyGroups}
                    />

                    <Select
                      mt={'sm'}
                      label='Job Family'
                      placeholder='Select Job Family'
                      data={jobFamilyOptions}
                      value={selectedJobFamily}
                      onChange={setSelectedJobFamily}
                      searchable
                      disabled={isLoadingJobFamilies || !selectedJobFamilyGroup}
                    />
                  </>
                )}

                <div style={{ position: 'relative', minHeight: '200px' }}>
                  <LoadingOverlay
                    visible={isLoadingHCParticipants}
                    overlayProps={{ radius: 'sm', blur: 2 }}
                  />

                  {selectedBusinessUnit && selectedLevel ? (
                    <>
                      {mappedHCParticipants.length > 0 && (
                        <>
                          <Table striped border={1} mt='md'>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th style={{ width: 50 }}>
                                  <Checkbox
                                    checked={selectAll}
                                    onChange={() => handleSelectAll(mappedHCParticipants)}
                                    indeterminate={selectedParticipants.length > 0 && !selectAll}
                                  />
                                </Table.Th>
                                <Table.Th>Name</Table.Th>
                                <Table.Th>Division</Table.Th>
                                <Table.Th>Type</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {mappedHCParticipants.map((participant) => (
                                <Table.Tr key={participant.binusianId}>
                                  <Table.Td>
                                    <Checkbox
                                      checked={selectedParticipants.some(
                                        (p) => p.binusianId === participant.binusianId
                                      )}
                                      onChange={() => handleToggleParticipant(participant)}
                                    />
                                  </Table.Td>
                                  <Table.Td>
                                    <Group>
                                      <Avatar color='#7B2183' radius='xl'>
                                        {participant.name && participant.name.length > 0
                                          ? participant.name.charAt(0)
                                          : '?'}
                                      </Avatar>
                                      <div>
                                        <Text size='sm' fw={500}>
                                          {participant.name || 'Unknown Name'}
                                        </Text>
                                        <Text size='xs' c='dimmed'>
                                          {participant.binusianId}
                                        </Text>
                                      </div>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td>{participant.division || 'N/A'}</Table.Td>
                                  <Table.Td>{participant.type || 'Unknown'}</Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                          {selectedParticipants.length > 0 && (
                            <Group justify='flex-end' mt='md'>
                              <Button
                                color='#7B2183'
                                onClick={() => setShowParticipantOptions(true)}
                              >
                                Proceed with {selectedParticipants.length} Selected Participant
                                {selectedParticipants.length !== 1 ? 's' : ''}
                              </Button>
                            </Group>
                          )}
                        </>
                      )}
                      {mappedHCParticipants.length === 0 && (
                        <Text c='dimmed' ta='center' mt='md'>
                          No participants found for the selected criteria
                        </Text>
                      )}
                    </>
                  ) : (
                    <Text c='dimmed' ta='center' mt='md'>
                      Please select{' '}
                      {participantType === 'Employee'
                        ? 'Business Unit, Level, Job Family Group, and Job Family'
                        : 'Faculty and Level'}{' '}
                      to view participants
                    </Text>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <Group mb='md'>
                  <Radio
                    label='Search Employees'
                    checked={searchEmployee && !searchLecturer}
                    onChange={() => {
                      setSearchEmployee(true);
                      setSearchLecturer(false);
                    }}
                  />
                  <Radio
                    label='Search Lecturers'
                    checked={!searchEmployee && searchLecturer}
                    onChange={() => {
                      setSearchEmployee(false);
                      setSearchLecturer(true);
                    }}
                  />
                  <Radio
                    label='Search Both'
                    checked={searchEmployee && searchLecturer}
                    onChange={() => {
                      setSearchEmployee(true);
                      setSearchLecturer(true);
                    }}
                  />
                </Group>

                <LoadingOverlay
                  visible={isLoadingEmplec}
                  overlayProps={{ radius: 'sm', blur: 2 }}
                />

                <TextInput
                  label='Search Participant'
                  placeholder='Search by name or ID...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.currentTarget.value)}
                  rightSection={<IconSearch size={16} />}
                />

                {debouncedSearchQuery.length > 2 && !isLoadingEmplec ? (
                  <Stack mt='md'>
                    {searchEmployee && (
                      <>
                        <Badge color='blue' size='lg' variant='light'>
                          Employees
                        </Badge>
                        {renderParticipantTable(employeeParticipants, 'employees')}
                      </>
                    )}

                    {searchLecturer && (
                      <>
                        <Badge color='green' size='lg' variant='light' mt='md'>
                          Lecturers
                        </Badge>
                        {renderParticipantTable(lecturerParticipants, 'lecturers')}
                      </>
                    )}

                    {employeeParticipants.length === 0 && lecturerParticipants.length === 0 && (
                      <Text c='dimmed' ta='center' mt='md'>
                        No participants found matching your search
                      </Text>
                    )}
                  </Stack>
                ) : (
                  <Text c='dimmed' ta='center' mt='md'>
                    {debouncedSearchQuery.length > 0
                      ? isLoadingEmplec
                        ? 'Searching...'
                        : 'No participants found matching your search'
                      : 'Enter at least 3 characters to search'}
                  </Text>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'participants' &&
          showParticipantOptions &&
          selectedParticipants.length > 0 && (
            <Paper withBorder p='md' radius='md'>
              <Stack>
                <Title order={4}>Selected Participants ({selectedParticipants.length})</Title>
                <Stack gap='sm'>
                  {selectedParticipants.map((participant) => (
                    <Group key={participant.binusianId} justify='space-between'>
                      <Group>
                        <Avatar color='#7B2183' radius='xl' size='lg'>
                          {participant.name && participant.name.length > 0
                            ? participant.name.charAt(0)
                            : '?'}
                        </Avatar>
                        <Box>
                          <Text fw={700}>{participant.name || 'Unknown Name'}</Text>
                          <Text size='sm'>{participant.email || ''}</Text>
                          <Text size='sm' c='dimmed'>
                            {participant.binusianId} • {participant.division || 'Unknown Division'}
                          </Text>
                        </Box>
                      </Group>
                      <Button
                        size='xs'
                        variant='subtle'
                        color='red'
                        onClick={() => handleToggleParticipant(participant)}
                      >
                        Remove
                      </Button>
                    </Group>
                  ))}
                </Stack>

                {modulesForAssignment.length > 0 && (
                  <>
                    <Text fw={600} mt='md'>
                      Assign {statusKurikulum ? 'Courses' : 'Modules'} to Participants:
                    </Text>

                    {/* Global Assignment Section */}
                    <Paper withBorder p='sm' radius='md' bg='blue.0'>
                      <Stack gap='xs'>
                        <Text size='sm' fw={600} c='blue'>
                          Select {statusKurikulum ? 'Courses' : 'Modules'} for All Participants:
                        </Text>
                        <Text size='xs' c='dimmed'>
                          These {statusKurikulum ? 'courses' : 'modules'} will be assigned to all
                          selected participants
                        </Text>

                        <Stack gap='xs' style={{ maxHeight: 150, overflowY: 'auto' }}>
                          {modulesForAssignment.map((module) => (
                            <Group
                              key={module.moduleId}
                              justify='space-between'
                              p='xs'
                              style={{
                                border: '1px solid #e9ecef',
                                borderRadius: '4px',
                                backgroundColor: globalModules.includes(module.moduleId)
                                  ? '#e3f2fd'
                                  : 'transparent'
                              }}
                            >
                              <Box style={{ flex: 1 }}>
                                <Text size='xs' fw={500}>
                                  {module.moduleName}
                                </Text>
                                {module.courseCode && (
                                  <Text size='xs' c='dimmed'>
                                    {module.courseCode}
                                  </Text>
                                )}
                              </Box>
                              <Button
                                size='xs'
                                variant={
                                  globalModules.includes(module.moduleId) ? 'filled' : 'outline'
                                }
                                color='blue'
                                onClick={() => toggleGlobalModule(module.moduleId)}
                              >
                                {globalModules.includes(module.moduleId) ? 'Selected' : 'Select'}
                              </Button>
                            </Group>
                          ))}
                        </Stack>

                        {globalModules.length > 0 && (
                          <Text size='xs' c='blue' fw={500}>
                            {globalModules.length} {statusKurikulum ? 'course' : 'module'}
                            {globalModules.length === 1 ? '' : 's'} selected for all participants
                          </Text>
                        )}
                      </Stack>
                    </Paper>

                    <Divider my='sm' />

                    {/* Individual Assignment Section */}
                    <Accordion variant='separated' radius='md'>
                      <Accordion.Item value='individual'>
                        <Accordion.Control>
                          <Text size='sm' fw={600}>
                            Customize Individual Assignments (Optional)
                          </Text>
                          <Text size='xs' c='dimmed' mt={4}>
                            Add additional {statusKurikulum ? 'courses' : 'modules'} for specific
                            participants
                          </Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap='md' style={{ maxHeight: 400, overflowY: 'auto' }}>
                            {selectedParticipants.map((participant) => {
                              const participantSpecificModules =
                                participantModules[participant.binusianId] || [];
                              const allParticipantModules = [
                                ...new Set([...globalModules, ...participantSpecificModules])
                              ];

                              return (
                                <Paper key={participant.binusianId} withBorder p='sm' radius='md'>
                                  <Group align='flex-start' gap='sm'>
                                    <Avatar color='#7B2183' radius='xl' size='md'>
                                      {participant.name && participant.name.length > 0
                                        ? participant.name.charAt(0)
                                        : '?'}
                                    </Avatar>
                                    <Box style={{ flex: 1 }}>
                                      <Text size='sm' fw={600}>
                                        {participant.name || 'Unknown Name'}
                                      </Text>
                                      <Text size='xs' c='dimmed'>
                                        {participant.binusianId}
                                      </Text>

                                      {globalModules.length > 0 && (
                                        <Text size='xs' c='blue' mt='xs'>
                                          Global: {globalModules.length}{' '}
                                          {statusKurikulum ? 'course' : 'module'}
                                          {globalModules.length === 1 ? '' : 's'}
                                        </Text>
                                      )}

                                      <Text size='xs' fw={500} mt='xs' c='green'>
                                        Additional {statusKurikulum ? 'Courses' : 'Modules'}:
                                      </Text>

                                      <Stack gap='xs' mt='xs'>
                                        {modulesForAssignment.map((module) => {
                                          const isGloballySelected = globalModules.includes(
                                            module.moduleId
                                          );
                                          const isSpecificallySelected =
                                            participantSpecificModules.includes(module.moduleId);

                                          return (
                                            <Group
                                              key={module.moduleId}
                                              justify='space-between'
                                              p='xs'
                                              style={{
                                                border: '1px solid #e9ecef',
                                                borderRadius: '4px',
                                                backgroundColor: isSpecificallySelected
                                                  ? '#f0f9ff'
                                                  : isGloballySelected
                                                    ? '#e3f2fd'
                                                    : 'transparent',
                                                opacity: isGloballySelected ? 0.7 : 1
                                              }}
                                            >
                                              <Box style={{ flex: 1 }}>
                                                <Text size='xs' fw={500}>
                                                  {module.moduleName}
                                                </Text>
                                                {module.courseCode && (
                                                  <Text size='xs' c='dimmed'>
                                                    {module.courseCode}
                                                  </Text>
                                                )}
                                                {isGloballySelected && !isSpecificallySelected && (
                                                  <Text size='xs' c='blue'>
                                                    (Global)
                                                  </Text>
                                                )}
                                              </Box>
                                              <Button
                                                size='xs'
                                                variant={
                                                  isSpecificallySelected ? 'filled' : 'outline'
                                                }
                                                color='green'
                                                disabled={isGloballySelected}
                                                onClick={() =>
                                                  toggleModule(
                                                    participant.binusianId,
                                                    module.moduleId
                                                  )
                                                }
                                              >
                                                {isSpecificallySelected
                                                  ? 'Added'
                                                  : isGloballySelected
                                                    ? 'Global'
                                                    : 'Add'}
                                              </Button>
                                            </Group>
                                          );
                                        })}
                                      </Stack>

                                      {allParticipantModules.length > 0 && (
                                        <Text size='xs' c='green' mt='xs' fw={500}>
                                          Total: {allParticipantModules.length}{' '}
                                          {statusKurikulum ? 'course' : 'module'}
                                          {allParticipantModules.length === 1 ? '' : 's'}
                                        </Text>
                                      )}
                                    </Box>
                                  </Group>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  </>
                )}
                {modulesForAssignment.length === 0 && (
                  <Text size='sm' c='dimmed'>
                    No {statusKurikulum ? 'courses' : 'modules'} available. Please select them first
                    in the main form.
                  </Text>
                )}

                <Text fw={600} mt='md'>
                  What would you like to do with these participants?
                </Text>

                <Group grow>
                  <Button
                    variant='outline'
                    color='#7B2183'
                    leftSection={<IconUsers size={16} />}
                    onClick={handleAddParticipantOnly}
                    disabled={selectedParticipants.some((p) => {
                      const participantSpecificModules = participantModules[p.binusianId] || [];
                      const totalModules = [
                        ...new Set([...globalModules, ...participantSpecificModules])
                      ];
                      return totalModules.length === 0;
                    })}
                  >
                    Add Participants
                  </Button>
                  {/* {selectedParticipants.length === 1 && (
                    <Button
                      variant='outline'
                      color='#7B2183'
                      leftSection={<IconUserCheck size={16} />}
                      onClick={() => setActiveTab('respondents')}
                    >
                      Select Respondents
                    </Button>
                  )} */}
                </Group>

                <Button
                  variant='subtle'
                  color='gray'
                  onClick={() => setShowParticipantOptions(false)}
                >
                  Cancel Selection
                </Button>
              </Stack>
            </Paper>
          )}

        {activeTab === 'respondents' && selectedParticipant && (
          <div style={{ position: 'relative' }}>
            <Button
              variant='outline'
              mb='md'
              leftSection={<IconArrowLeft size={16} />}
              onClick={handleBackToParticipants}
            >
              Back to Participants
            </Button>

            <Group mb='md'>
              <Text fw={500}>Filter Respondents:</Text>
              <Radio.Group defaultValue='all'>
                <Group>
                  <Radio
                    value='peer'
                    label='Peer'
                    checked={
                      respondentFilters.isPeer &&
                      !respondentFilters.isSupervisor &&
                      !respondentFilters.isSubordinate
                    }
                    onChange={() =>
                      setRespondentFilters({
                        isPeer: true,
                        isSupervisor: false,
                        isSubordinate: false
                      })
                    }
                  />
                  <Radio
                    value='supervisor'
                    label='Supervisor'
                    checked={
                      !respondentFilters.isPeer &&
                      respondentFilters.isSupervisor &&
                      !respondentFilters.isSubordinate
                    }
                    onChange={() =>
                      setRespondentFilters({
                        isPeer: false,
                        isSupervisor: true,
                        isSubordinate: false
                      })
                    }
                  />
                  <Radio
                    value='subordinate'
                    label='Subordinate'
                    checked={
                      !respondentFilters.isPeer &&
                      !respondentFilters.isSupervisor &&
                      respondentFilters.isSubordinate
                    }
                    onChange={() =>
                      setRespondentFilters({
                        isPeer: false,
                        isSupervisor: false,
                        isSubordinate: true
                      })
                    }
                  />
                  <Radio
                    value='all'
                    label='All Types'
                    checked={
                      respondentFilters.isPeer &&
                      respondentFilters.isSupervisor &&
                      respondentFilters.isSubordinate
                    }
                    onChange={() =>
                      setRespondentFilters({
                        isPeer: true,
                        isSupervisor: true,
                        isSubordinate: true
                      })
                    }
                  />
                </Group>
              </Radio.Group>
            </Group>

            <LoadingOverlay visible={isLoadingRespondents} />

            {respondents?.data && respondents.data.length > 0 ? (
              <Table striped border={1}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th style={{ width: 80 }}>Select</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {respondents.data.map((respondent: any) => {
                    const mappedRespondent: Participant = {
                      binusianId: respondent.binusianId,
                      name: respondent.nama || 'Unknown',
                      type: respondent.respondentType || 'Unknown',
                      email: respondent.email
                    };

                    return (
                      <Table.Tr key={respondent.binusianId}>
                        <Table.Td>
                          <Group>
                            <Avatar color='#7B2183' radius='xl'>
                              {mappedRespondent.name && mappedRespondent.name.length > 0
                                ? mappedRespondent.name.charAt(0)
                                : '?'}
                            </Avatar>
                            <div>
                              <Text size='sm' fw={500}>
                                {mappedRespondent.name}
                              </Text>
                              <Text size='xs' c='dimmed'>
                                {mappedRespondent.binusianId}
                              </Text>
                            </div>
                          </Group>
                        </Table.Td>
                        <Table.Td>{mappedRespondent.email}</Table.Td>
                        <Table.Td>{mappedRespondent.type}</Table.Td>
                        <Table.Td>
                          <Button
                            size='xs'
                            variant={
                              selectedRespondents.some(
                                (r) => generateUniqueId(r) === generateUniqueId(mappedRespondent)
                              )
                                ? 'filled'
                                : 'outline'
                            }
                            color='#7B2183'
                            onClick={() => handleToggleRespondent(mappedRespondent)}
                          >
                            {selectedRespondents.some(
                              (r) => generateUniqueId(r) === generateUniqueId(mappedRespondent)
                            )
                              ? 'Selected'
                              : 'Select'}
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            ) : (
              !isLoadingRespondents && (
                <Text c='dimmed' ta='center' mt='md'>
                  No respondents found for this participant
                </Text>
              )
            )}
          </div>
        )}

        <Group justify='flex-end' mt='md'>
          <Button variant='outline' onClick={onClose} color='#7B2183'>
            Cancel
          </Button>

          {activeTab === 'respondents' && (
            <Button onClick={handleSubmit} color='#7B2183' disabled={!selectedParticipant}>
              {`Submit with ${selectedRespondents.length} Respondent${selectedRespondents.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};

export default ParticipantModal;
